import React, { useEffect, useMemo, useRef, useCallback } from "react";
import ReactQuill, { Quill } from "react-quill-new";
import imageCompression from "browser-image-compression"; // npm install browser-image-compression
import "react-quill-new/dist/quill.snow.css";

export type TextEditorToolbarModules = {
  toolbar?: any;
  clipboard?: any;
};

export interface TextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  minHeightPx?: number;
  maxHeightPx?: number;
  fontSizePx?: number;
  zIndex?: number;
  imageMaxWidthPx?: number;
  imageMaxHeightPx?: number;
  className?: string;
  wrapperClassName?: string;
  modules?: TextEditorToolbarModules;
  formats?: string[];
  blockImages?: boolean;
}

const DEFAULT_FORMATS = ["header", "bold", "italic", "underline", "list", "image"];

const DEFAULT_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["clean"],
  ["image"],
];

const TextEditor: React.FC<TextEditorProps> = ({
  value,
  onChange,
  placeholder = "",
  disabled = false,
  readOnly = false,
  minHeightPx = 120,
  maxHeightPx = 200,
  fontSizePx = 13,
  zIndex = 9999,
  imageMaxWidthPx = 320,
  imageMaxHeightPx = 200,
  className,
  wrapperClassName,
  modules,
  formats,
  blockImages = false,
}) => {
  const quillRef = useRef<any>(null);

  // --- Core Compression & Insertion Logic ---
  const compressAndInsertImage = useCallback(async (file: File) => {
    const options = {
      maxSizeMB: 0.04,           // Aim for 40KB
      maxWidthOrHeight: 800,    // Resize if larger
      useWebWorker: true,
      fileType: "image/jpeg",   // CRITICAL: JPEGs result in shorter strings
      initialQuality: 0.6,      // 60% quality is usually plenty for descriptions
    };
  
    try {
      const compressedFile = await imageCompression(file, options);
      const reader = new FileReader();
      reader.readAsDataURL(compressedFile);
      reader.onloadend = () => {
        const base64data = reader.result as string;
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, "image", base64data);
          quill.setSelection(range.index + 1);
        }
      };
    } catch (error) {
      console.error("Compression Error:", error);
    }
  }, []);

  // Handler for Toolbar button
  const selectLocalImage = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) await compressAndInsertImage(file);
    };
  }, [compressAndInsertImage]);

  const quillModules = useMemo(() => {
    const Delta = Quill.import("delta");

    const baseClipboardMatchers: any[] = [
      [
        Node.TEXT_NODE,
        (_node: any, delta: any) => {
          if (!delta?.ops) return delta;
          delta.ops = delta.ops.map((op: any) => {
            if (typeof op?.insert !== "string") return op;
            return {
              ...op,
              insert: op.insert.replace(/\u00A0/g, " ").replace(/\u200B/g, ""),
            };
          });
          return delta;
        },
      ],
    ];

    if (blockImages) {
      baseClipboardMatchers.push(["img", () => new Delta()]);
    }

    const mergedClipboard = {
      ...(modules?.clipboard ?? {}),
      matchers: [...(modules?.clipboard?.matchers ?? []), ...baseClipboardMatchers],
    };

    let toolbarConfig = modules?.toolbar ?? DEFAULT_TOOLBAR;
    if (blockImages && Array.isArray(toolbarConfig)) {
      toolbarConfig = toolbarConfig
        .map((group) => {
          if (Array.isArray(group)) return group.filter((item) => item !== "image");
          return group;
        })
        .filter((group) => !Array.isArray(group) || group.length > 0);
    }

    return {
      toolbar: {
        container: toolbarConfig,
        handlers: {
          image: selectLocalImage,
        },
      },
      clipboard: mergedClipboard,
      ...(modules ?? {}),
    } as Record<string, unknown>;
  }, [modules, blockImages, selectLocalImage]);

  const quillFormats = useMemo(() => {
    const baseFormats = formats ?? DEFAULT_FORMATS;
    return blockImages ? baseFormats.filter((f) => f !== "image") : baseFormats;
  }, [formats, blockImages]);

  // --- Effect to handle Paste and Drop ---
  useEffect(() => {
    const editor = quillRef.current?.getEditor?.();
    if (!editor) return;

    const root: HTMLElement = editor.root;
    if (!root) return;

    const handleFiles = async (fileList: FileList | null | undefined) => {
      if (!fileList) return;
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList.item(i);
        if (file?.type?.startsWith("image/")) {
            // If blockImages is true, we stop here.
            // Otherwise, we compress and insert.
            if (!blockImages) {
                await compressAndInsertImage(file);
            }
        }
      }
    };

    const onPaste = (e: ClipboardEvent) => {
      const files = e.clipboardData?.files;
      if (files && files.length > 0) {
        e.preventDefault(); // Stop default Base64 insertion
        handleFiles(files);
      }
    };

    const onDrop = (e: DragEvent) => {
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        e.preventDefault(); // Stop default Base64 insertion
        handleFiles(files);
      }
    };

    root.addEventListener("paste", onPaste as any);
    root.addEventListener("drop", onDrop as any);

    return () => {
      root.removeEventListener("paste", onPaste as any);
      root.removeEventListener("drop", onDrop as any);
    };
  }, [blockImages, compressAndInsertImage]);

  return (
    <div className={wrapperClassName ?? "comment-editor2-quill-wrap"}>
      <style>{`
        .comment-editor2-quill-wrap .ql-toolbar { position: relative; z-index: 100; border-radius: 6px; background-color: #F9FAFB; }
        .comment-editor2-quill-wrap .ql-picker-options { z-index: ${zIndex} !important; }
        .comment-editor2-quill-wrap .ql-container { border-radius: 6px; min-height: ${minHeightPx}px; }
        .comment-editor2-quill-wrap .ql-editor { min-height: ${minHeightPx}px; max-height: ${maxHeightPx}px; overflow-y: auto; }
        .comment-editor2-quill-wrap .ql-container,
        .comment-editor2-quill-wrap .ql-editor { font-family: inherit; font-size: ${fontSizePx}px; }
        .comment-editor2-quill-wrap .ql-editor * { font-family: inherit; }
        .comment-editor2-quill-wrap .ql-editor img {
          max-width: ${imageMaxWidthPx}px;
          max-height: ${imageMaxHeightPx}px;
          width: auto;
          height: auto;
          object-fit: contain;
          display: block;
        }
      `}</style>

      <ReactQuill
        ref={quillRef}
        value={value}
        onChange={onChange}
        modules={quillModules}
        formats={quillFormats}
        theme="snow"
        placeholder={placeholder}
        readOnly={readOnly || disabled}
        className={className}
      />
    </div>
  );
};

export default TextEditor;